import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { ProjectData, NotificationRecipient } from '../types';
import { useProject } from '../hooks/useProject';
// import { useAuth } from '../hooks/useAuth'; // Removed - using user prop instead
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useEventDrivenAutoSave } from '../hooks/useEventDrivenAutoSave';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { mergeFormDataPreferNonEmpty, formDataHasSubstantiveProgress, stripBlankFormData } from '../utils/wizardLocalStorage';
import { ChevronLeft, ChevronRight, Save, CheckCircle } from 'lucide-react';

// Import form configuration and step components
import { formConfigs } from '../config/formConfigs';
import DynamicStep from './wizard-steps/DynamicStep';
import NotificationPreferencesStep from './wizard-steps/NotificationPreferencesStep';
import ShopConfigurationStep from './wizard-steps/ShopConfigurationStep';
import { WizardValidationBanner } from './WizardValidationBanner';

// Step configuration interface
interface StepConfig {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  fields: string[];
}

interface FormWizardProps {
  appType: 'versirent' | 'idealone';
  jiraKey: string;
  onComplete?: (data: ProjectData['formData']) => void;
  user?: any; // User from secure auth
}

export default function FormWizard({ appType, jiraKey, onComplete, user }: FormWizardProps) {
  // const { user } = useAuth(); // Removed - using user prop instead
  const { project, updateFormData, submitForm, loading: projectLoading } = useProject(jiraKey);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isSavingOnNavigation, setIsSavingOnNavigation] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecipient[]>(project?.notifications || []); // IS-1427 Refactored

  // Get form configuration for this app type
  const formConfig = formConfigs[appType];
  if (!formConfig) {
    return <div>Invalid app type: {appType}</div>;
  }

  // Generate steps dynamically from configuration
  const STEPS: StepConfig[] = formConfig.steps.map((step) => ({
    ...step,
    // IS-1461: Use custom components for notifications and shop steps
    component: step.id === 'notifications' 
      ? NotificationPreferencesStep 
      : step.id === 'shop'
      ? ShopConfigurationStep
      : DynamicStep,
  }));

  // Update notifications when project loads
  useEffect(() => {
    if (project?.notifications) {
      setNotifications(project.notifications);
    }
  }, [project?.notifications]);

  // Local storage and auto-save hooks
  const { isOnline } = useOnlineStatus();
  const { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage, migrateFromPendingKey, removePendingKey, hasLocalData, lastLocalSave } = useLocalStorage(jiraKey);
  const { saveFieldDebounced, saveAllFields, lastSaved: autoSaveLastSaved, isSaving, saveError } = useEventDrivenAutoSave({
    jiraKey,
    updateFormData,
    saveToLocalStorage,
    currentStep,
    completedSteps,
    isOnline,
  });

  // Initialize form with existing data or defaults (memoized to prevent infinite loops)
  const defaultValues: Partial<ProjectData['formData']> = useMemo(() => ({
    appName: '',
    logoUrl: '',
    appIconUrl: '',
    useLogoAsIcon: false,
    primaryColor: '#3B82F6',
    // IMA-13: no fake default — only include secondary when the customer sets it
    secondaryColor: '',
    companyDescription: '',
    copyrightMsg: `© Copyright ${new Date().getFullYear()}`,
    ecommerceType: 'integrated',
    redirectWebsiteUrl: '',
    termsUrl: '',
    privacyUrl: '',
    appStoreContactEmail: '',
    appStoreContactPhone: '',
    versiRentStoreId: '',
    versiRentUsername: '',
    versiRentPassword: '',
    versiRentActiveAgreement: false,
    testerEmails: [],
    googlePlayBrandAssetAgreement: '',
    appleDeveloperAccountName: '',
    appleTeamId: '',
    appleUserMwhorey: false,
    appleUserNmore: false,
    appleUserDev: false,
    appleUserCstack: false,
    appleUserDscarafiotti: false,
    teamKeyEnabled: false,
    notificationEmail: '',
    enableEmailNotifications: false,
    notificationPhone: '',
    enableSmsNotifications: false,
    ...project?.formData,
  }), [project?.formData]);

  const methods = useForm<ProjectData['formData']>({
    defaultValues,
    mode: 'onChange',
  });

  // Load from local storage on mount (only once)
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);
  
  // Smart navigation: determine the best step to show the user from Firebase data
  const getInitialStepFromProjectData = useCallback(() => {
    if (!project?.formData) return 0;
    
    console.log('🔄 Calculating furthest completed step from Firebase project data');
    let furthestStep = 0;
    
    // Special validation for logo/icon relationship
    // Rule: Logo is required AND (checkbox is checked OR icon is uploaded)
    const hasLogo = !!(project.formData.logoUrl && project.formData.logoUrl.trim().length > 0);
    const checkboxChecked = project.formData.useLogoAsIcon === true;
    const hasIcon = !!(project.formData.appIconUrl && project.formData.appIconUrl.trim().length > 0);
    const logoIconValid = hasLogo && (checkboxChecked || hasIcon);
    
    // Check each step to see how far the user got based on project data
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      if (!step) continue;
      
      let stepComplete = true;
      
      // Check if all required fields for this step are completed
      for (const field of step.fields) {
        const value = project.formData[field as keyof ProjectData['formData']];
        
        // Skip optional fields
        if (['companyDescription', 'copyrightMsg', 'appleAccountInvitesSection', 'useLogoAsIcon', 'secondaryColor'].includes(field)) {
          continue;
        }
        
        // Special handling for logo/icon relationship
        if (field === 'appIconUrl' || field === 'logoUrl') {
          if (!logoIconValid) {
            stepComplete = false;
            break;
          }
          continue;
        }
        if (field === 'redirectWebsiteUrl' && project.formData.ecommerceType === 'integrated') {
          continue; // Not required if using integrated shop
        }
        if (field === 'notificationEmail' && !project.formData.enableEmailNotifications) {
          continue; // Not required if email notifications disabled
        }
        if (field === 'notificationPhone' && !project.formData.enableSmsNotifications) {
          continue; // Not required if SMS notifications disabled
        }
        
        // Check if field has a value
        if (typeof value === 'boolean') {
          if (value !== true) {
            stepComplete = false;
            break;
          }
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            stepComplete = false;
            break;
          }
        } else if (typeof value === 'string') {
          if (value.trim().length === 0) {
            stepComplete = false;
            break;
          }
        } else if (value === null || value === undefined) {
          stepComplete = false;
          break;
        }
      }
      
      if (stepComplete) {
        furthestStep = i + 1; // Move to next step if current is complete
      } else {
        break; // Stop at first incomplete step
      }
    }
    
    // Don't go beyond the last step
    const targetStep = Math.min(furthestStep, STEPS.length - 1);
    console.log('🔄 Calculated furthest step from Firebase data:', targetStep);
    return targetStep;
  }, [project?.formData]);
  
  useEffect(() => {
    if (!jiraKey || hasLoadedFromStorage) return;
    // IMA-20: wait for Firestore project so previousPendingKey migration isn't skipped
    if (projectLoading) return;

    const pendingKey = project?.previousPendingKey;
    const clearPreviousPendingKey = () =>
      fetch('https://us-central1-ima-onboard.cloudfunctions.net/updateProject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey,
          updates: { previousPendingKey: null },
        }),
      });

    // IMA-20: after Link Jira, migrate PENDING-* wizard cache into the real key
    if (pendingKey) {
      // Keep PENDING until Firestore persist succeeds so a failed save can retry
      const migrated = migrateFromPendingKey(pendingKey, { removeSource: false });
      if (migrated) {
        const mergedData = mergeFormDataPreferNonEmpty(
          { ...defaultValues, ...project.formData },
          migrated.formData
        );
        methods.reset(mergedData);
        setCurrentStep(migrated.currentStep || 0);
        setCompletedSteps(migrated.completedSteps || []);
        setLastSaved(migrated.lastSaved ? new Date(migrated.lastSaved) : null);
        setHasLoadedFromStorage(true);
        void (async () => {
          try {
            // Only persist substantive fields; prefer any live edits that landed before this write
            const toPersist = stripBlankFormData(
              mergeFormDataPreferNonEmpty(mergedData, methods.getValues())
            );
            if (toPersist) {
              await updateFormData(toPersist as ProjectData['formData']);
            }
            removePendingKey(pendingKey);
            await clearPreviousPendingKey();
          } catch (err) {
            console.warn(
              'IMA-20: failed to persist migrated formData; keeping PENDING + previousPendingKey',
              err
            );
          }
        })();
        return;
      }
      // No-op migrate: this browser has no PENDING cache. Do not clear previousPendingKey
      // unless Firestore already has progress — clearing would kill recovery on the device
      // that still holds wizard-progress-PENDING-*.
    }

    const localData = loadFromLocalStorage();

    if (localData) {
      console.log('🔄 Loading form data from local storage');
      // Priority 1: Load from local storage (most recent work)
      // IMA-20: blank local fields must not wipe project/defaults
      const mergedData = mergeFormDataPreferNonEmpty(
        { ...defaultValues, ...project?.formData },
        localData.formData
      );
      methods.reset(mergedData);
      setCurrentStep(localData.currentStep || 0);
      setCompletedSteps(localData.completedSteps || []);
      setLastSaved(localData.lastSaved ? new Date(localData.lastSaved) : null);
      setHasLoadedFromStorage(true);
    } else if (project?.formData) {
      console.log('🔄 No local storage found, loading from Firebase project data');
      // Priority 2: Load from Firebase (fallback when local storage is cleared)
      const mergedData = { ...defaultValues, ...project.formData };
      methods.reset(mergedData);

      // Calculate the furthest completed step from Firebase data
      const initialStep = getInitialStepFromProjectData();
      setCurrentStep(initialStep);

      // Calculate completed steps from project data
      const newCompletedSteps: number[] = [];
      for (let i = 0; i < initialStep; i++) {
        newCompletedSteps.push(i);
      }
      setCompletedSteps(newCompletedSteps);
      setHasLoadedFromStorage(true);
    } else {
      console.log('🔄 No data found anywhere, starting fresh');
      // Priority 3: Start fresh (new user or no data)
      setCurrentStep(0);
      setCompletedSteps([]);
      setHasLoadedFromStorage(true);
    }

    if (pendingKey && formDataHasSubstantiveProgress(project?.formData)) {
      void clearPreviousPendingKey().catch((err) =>
        console.warn('IMA-20: failed to clear previousPendingKey after no-op migrate', err)
      );
    }
  }, [jiraKey, hasLoadedFromStorage, projectLoading, loadFromLocalStorage, migrateFromPendingKey, removePendingKey, methods, defaultValues, getInitialStepFromProjectData, project?.formData, project?.previousPendingKey, updateFormData]);

  const { watch, formState: { isDirty } } = methods;

  // Enhanced auto-save functionality with local storage
  const autoSave = useCallback(async () => {
    if (isDirty && user) {
      try {
        const formData = watch();
        await saveAllFields(formData);
        setLastSaved(new Date());
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }
  }, [isDirty, user, watch, saveAllFields]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(autoSave, 30000);
    return () => clearInterval(interval);
  }, [autoSave]);

  // Watch for form changes and auto-save individual fields (with proper debouncing)
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name && type === 'change') {
        const fieldValue = value[name as keyof typeof value];
        
        // Use debounced save for all fields to prevent excessive saves
        saveFieldDebounced(name, fieldValue);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, saveFieldDebounced]);

  // Check if current step is complete (only required fields)
  const isStepComplete = useCallback((stepIndex: number) => {
    const step = STEPS[stepIndex];
    if (!step) return false;

    const formData = watch();
    
    // IS-1460: Special handling for notifications step - validate notifications array instead of old fields
    if (step.id === 'notifications') {
      // Notifications step is optional - always considered complete
      // But if notifications are provided, validate they're properly formatted
      if (notifications.length === 0) {
        return true; // Optional step - no notifications is valid
      }
      // If notifications exist, validate each one has proper formatting
      return notifications.every(n => {
        if (n.channel === 'email') {
          const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return n.recipient && emailPattern.test(n.recipient);
        } else if (n.channel === 'sms') {
          const phonePattern = /^\d{10,15}$/;
          return n.recipient && phonePattern.test(n.recipient) && n.countryCode;
        }
        return false;
      });
    }
    
    // Define optional fields that should not block step completion
    const optionalFields = [
      'companyDescription',
      'copyrightMsg',
      'appleAccountInvitesSection',
      'useLogoAsIcon', // This is a UI control, not a required field
      'secondaryColor', // IMA-13: optional; empty must not block shop step
      'enableEmailNotifications', // Optional - user can choose to enable or not
      'enableSmsNotifications', // Optional - user can choose to enable or not
      'notificationPhoneCountryCode' // Optional - only required if SMS enabled
    ];
    
    // Special validation for logo/icon relationship
    // Rule: Logo is required AND (checkbox is checked OR icon is uploaded)
    const hasLogo = !!(formData.logoUrl && formData.logoUrl.trim().length > 0);
    const checkboxChecked = formData.useLogoAsIcon === true;
    const hasIcon = !!(formData.appIconUrl && formData.appIconUrl.trim().length > 0);
    const logoIconValid = hasLogo && (checkboxChecked || hasIcon);
    
    // Get field definitions for this step
    const stepFieldNames = step.fields; // Array of field names like ['appName', 'logoUrl', ...]
    
    return stepFieldNames.every(fieldName => {
      // Skip optional fields - they don't block step completion
      if (optionalFields.includes(fieldName)) {
        return true;
      }

      // Special handling for logo/icon relationship (before formConfig.required)
      if (fieldName === 'appIconUrl') {
        return logoIconValid;
      }
      if (fieldName === 'logoUrl') {
        return logoIconValid;
      }

      // Conditional required: Custom Shop URL (config marks required:false)
      if (fieldName === 'redirectWebsiteUrl') {
        if (formData.ecommerceType === 'integrated') {
          return true;
        }
        const url = formData.redirectWebsiteUrl;
        return typeof url === 'string' && url.trim().length > 0;
      }

      // Conditional required: notification contact fields
      if (fieldName === 'notificationEmail' && !formData.enableEmailNotifications) {
        return true;
      }
      if (fieldName === 'notificationPhone' && !formData.enableSmsNotifications) {
        return true;
      }

      // Honor formConfig.required (e.g. versiRentStoreId) after conditionals
      const fieldConfig = formConfig.fields.find((f) => f.name === fieldName);
      if (fieldConfig && !fieldConfig.required) {
        return true;
      }

      const value = formData[fieldName as keyof ProjectData['formData']];
      if (typeof value === 'boolean') return value === true; // Required checkboxes must be checked
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined;
    });
  }, [watch, notifications, formConfig.fields]);

  // Calculate overall completion percentage (only required fields)
  const getOverallCompletion = useCallback(() => {
    const formData = watch();
    let totalFields = 0;
    let completedFields = 0;

    // Define optional fields that should not count toward completion
    const optionalFields = [
      'companyDescription',
      'copyrightMsg',
      'appleAccountInvitesSection',
      'useLogoAsIcon', // This is a UI control, not a required field
      'secondaryColor', // IMA-13: optional; empty must not block shop step
    ];

    // Special validation for logo/icon relationship
    // Rule: Logo is required AND (checkbox is checked OR icon is uploaded)
    const hasLogo = !!(formData.logoUrl && formData.logoUrl.trim().length > 0);
    const checkboxChecked = formData.useLogoAsIcon === true;
    const hasIcon = !!(formData.appIconUrl && formData.appIconUrl.trim().length > 0);
    const logoIconValid = hasLogo && (checkboxChecked || hasIcon);

    STEPS.forEach((step) => {
      // IS-1460: Special handling for notifications step - skip old fields, count as complete (optional step)
      if (step.id === 'notifications') {
        return; // Skip notifications step fields - it's optional
      }
      
      step.fields.forEach(field => {
        // Skip optional fields - they don't count toward completion
        if (optionalFields.includes(field)) {
          return;
        }

        // Special handling for logo/icon relationship (before formConfig.required)
        if (field === 'appIconUrl') {
          totalFields++;
          if (logoIconValid) completedFields++;
          return;
        }
        if (field === 'logoUrl') {
          totalFields++;
          if (logoIconValid) completedFields++;
          return;
        }

        // Conditional required: Custom Shop URL (config marks required:false)
        if (field === 'redirectWebsiteUrl') {
          totalFields++;
          if (formData.ecommerceType === 'integrated') {
            completedFields++;
          } else {
            const url = formData.redirectWebsiteUrl;
            if (typeof url === 'string' && url.trim().length > 0) completedFields++;
          }
          return;
        }

        // Conditional required: notification contact fields
        if (field === 'notificationEmail' && !formData.enableEmailNotifications) {
          totalFields++;
          completedFields++;
          return;
        }
        if (field === 'notificationPhone' && !formData.enableSmsNotifications) {
          totalFields++;
          completedFields++;
          return;
        }

        // Honor formConfig.required (e.g. versiRentStoreId) after conditionals
        const fieldConfig = formConfig.fields.find((f) => f.name === field);
        if (fieldConfig && !fieldConfig.required) {
          return;
        }

        totalFields++;
        const value = formData[field as keyof ProjectData['formData']];

        // Special handling for different field types
        if (typeof value === 'boolean') {
          if (value === true) completedFields++; // Required checkboxes must be checked
        } else if (Array.isArray(value)) {
          if (value.length > 0) completedFields++;
        } else if (typeof value === 'string') {
          if (value.trim().length > 0) completedFields++;
        } else if (value !== null && value !== undefined) {
          completedFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  }, [watch, formConfig.fields]);

  // Calculate step completion percentage for any step
  const getStepCompletion = useCallback((stepIndex: number) => {
    const step = STEPS[stepIndex];
    if (!step) return 0;

    const formData = watch();
    let totalFields = 0;
    let completedFields = 0;

    // IS-1460: Special handling for notifications step - check notifications array instead of old fields
    if (step.id === 'notifications') {
      // Notifications step is optional - always 100% complete
      // But show progress based on valid notifications if any are added
      if (notifications.length === 0) {
        return 100; // Optional - no notifications needed
      }
      // Count valid notifications
      const validNotifications = notifications.filter(n => {
        if (n.channel === 'email') {
          const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return n.recipient && emailPattern.test(n.recipient);
        } else if (n.channel === 'sms') {
          const phonePattern = /^\d{10,15}$/;
          return n.recipient && phonePattern.test(n.recipient) && n.countryCode;
        }
        return false;
      });
      // If all notifications are valid, step is complete
      return validNotifications.length === notifications.length ? 100 : Math.round((validNotifications.length / notifications.length) * 100);
    }

    // Define optional fields that should not count toward completion
    const optionalFields = [
      'companyDescription',
      'copyrightMsg',
      'appleAccountInvitesSection',
      'useLogoAsIcon', // This is a UI control, not a required field
      'secondaryColor', // IMA-13: optional; empty must not block shop step
    ];
    
    // Special validation for logo/icon relationship
    // Rule: Logo is required AND (checkbox is checked OR icon is uploaded)
    const hasLogo = !!(formData.logoUrl && formData.logoUrl.trim().length > 0);
    const checkboxChecked = formData.useLogoAsIcon === true;
    const hasIcon = !!(formData.appIconUrl && formData.appIconUrl.trim().length > 0);
    const logoIconValid = hasLogo && (checkboxChecked || hasIcon);

    step.fields.forEach(field => {
      // Skip optional fields - they don't count toward completion
      if (optionalFields.includes(field)) {
        return;
      }

      // Special handling for logo/icon relationship (before formConfig.required)
      if (field === 'appIconUrl') {
        totalFields++;
        if (logoIconValid) completedFields++;
        return;
      }
      if (field === 'logoUrl') {
        totalFields++;
        if (logoIconValid) completedFields++;
        return;
      }

      // Conditional required: Custom Shop URL (config marks required:false)
      if (field === 'redirectWebsiteUrl') {
        totalFields++;
        if (formData.ecommerceType === 'integrated') {
          completedFields++;
        } else {
          const url = formData.redirectWebsiteUrl;
          if (typeof url === 'string' && url.trim().length > 0) completedFields++;
        }
        return;
      }

      // Conditional required: notification contact fields
      if (field === 'notificationEmail' && !formData.enableEmailNotifications) {
        totalFields++;
        completedFields++;
        return;
      }
      if (field === 'notificationPhone' && !formData.enableSmsNotifications) {
        totalFields++;
        completedFields++;
        return;
      }

      // Honor formConfig.required (e.g. versiRentStoreId) after conditionals
      const fieldConfig = formConfig.fields.find((f) => f.name === field);
      if (fieldConfig && !fieldConfig.required) {
        return;
      }

      totalFields++;
      const value = formData[field as keyof ProjectData['formData']];

      // Special handling for different field types
      if (typeof value === 'boolean') {
        if (value === true) completedFields++; // Required checkboxes must be checked
      } else if (Array.isArray(value)) {
        if (value.length > 0) completedFields++;
      } else if (typeof value === 'string') {
        if (value.trim().length > 0) completedFields++;
      } else if (value !== null && value !== undefined) {
        completedFields++;
      }
    });

    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  }, [watch, notifications, formConfig.fields]);

  // Calculate current step completion percentage
  const getCurrentStepCompletion = useCallback(() => {
    return getStepCompletion(currentStep);
  }, [getStepCompletion, currentStep]);

  // Update completed steps when form data changes
  useEffect(() => {
    const newCompletedSteps: number[] = [];
    STEPS.forEach((_, index) => {
      if (isStepComplete(index)) {
        newCompletedSteps.push(index);
      }
    });
    setCompletedSteps(newCompletedSteps);
  }, [watch(), isStepComplete]);

  // Navigation functions
  const goToNextStep = useCallback(async () => {
    if (currentStep < STEPS.length - 1) {
      // Validate current step before proceeding
      const step = STEPS[currentStep];
      const formData = watch();
      
      // Define optional fields that should not block step completion
      const optionalFields = [
        'companyDescription',
        'copyrightMsg',
        'appleAccountInvitesSection',
        'useLogoAsIcon', // This is a UI control, not a required field
        'secondaryColor', // IMA-13: optional; empty must not block shop step
      ];
      
      // Special validation for logo/icon relationship
      // Rule: Logo is required AND (checkbox is checked OR icon is uploaded)
      const hasLogo = formData.logoUrl && formData.logoUrl.trim().length > 0;
      const checkboxChecked = formData.useLogoAsIcon === true;
      const hasIcon = formData.appIconUrl && formData.appIconUrl.trim().length > 0;
      const logoIconValid = hasLogo && (checkboxChecked || hasIcon);
      
      // Collect all invalid required fields
      const invalidFieldsList: string[] = [];
      let firstInvalidField = null;
      
      for (const field of step.fields) {
        // Skip optional fields
        if (optionalFields.includes(field)) {
          continue;
        }

        // Special handling for logo/icon relationship (before formConfig.required)
        if (field === 'appIconUrl' || field === 'logoUrl') {
          if (!logoIconValid) {
            invalidFieldsList.push(field);
            if (!firstInvalidField) {
              firstInvalidField = field;
            }
          }
          continue;
        }

        // Conditional required: Custom Shop URL (config marks required:false)
        if (field === 'redirectWebsiteUrl') {
          if (formData.ecommerceType === 'integrated') {
            continue;
          }
          const url = formData.redirectWebsiteUrl;
          if (!(typeof url === 'string' && url.trim().length > 0)) {
            invalidFieldsList.push(field);
            if (!firstInvalidField) {
              firstInvalidField = field;
            }
          }
          continue;
        }

        if (field === 'notificationEmail' && !formData.enableEmailNotifications) {
          continue; // Not required if email notifications disabled
        }
        if (field === 'notificationPhoneCountryCode' && !formData.enableSmsNotifications) {
          continue; // Not required if SMS notifications disabled
        }
        if (field === 'notificationPhone' && !formData.enableSmsNotifications) {
          continue; // Not required if SMS notifications disabled
        }

        // Honor formConfig.required (e.g. versiRentStoreId) after conditionals
        const fieldConfig = formConfig.fields.find((f) => f.name === field);
        if (fieldConfig && !fieldConfig.required) {
          continue;
        }

        const value = formData[field as keyof ProjectData['formData']];
        let isValid = false;
        
        if (typeof value === 'boolean') {
          isValid = value === true; // Required checkboxes must be checked
        } else if (Array.isArray(value)) {
          isValid = value.length > 0;
        } else if (typeof value === 'string') {
          isValid = value.trim().length > 0;
        } else {
          isValid = value !== null && value !== undefined;
        }
        
        if (!isValid) {
          invalidFieldsList.push(field);
          if (!firstInvalidField) {
            firstInvalidField = field;
          }
        }
      }
      
      if (invalidFieldsList.length > 0) {
        // Set invalid fields for visual indicators
        setInvalidFields(invalidFieldsList);
        
        // Focus and scroll to the first invalid field
        if (firstInvalidField) {
          const fieldElement = document.getElementById(firstInvalidField);
          if (fieldElement) {
            fieldElement.focus();
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        
        return; // Don't proceed to next step
      }
      
      // Clear any existing invalid fields and save progress before proceeding
      setInvalidFields([]);
      
      // Save current progress before moving to next step
      setIsSavingOnNavigation(true);
      try {
        await saveAllFields(formData);
        console.log('💾 Progress saved before advancing to next step');
      } catch (error) {
        console.error('Failed to save progress before next step:', error);
        // Continue to next step even if save fails
      } finally {
        setIsSavingOnNavigation(false);
      }
      
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, watch, saveAllFields, formConfig.fields]);

  const goToPreviousStep = useCallback(async () => {
    if (currentStep > 0) {
      // Save current progress before going back
      setIsSavingOnNavigation(true);
      try {
        const formData = watch();
        await saveAllFields(formData);
        console.log('💾 Progress saved before going to previous step');
      } catch (error) {
        console.error('Failed to save progress before previous step:', error);
        // Continue to previous step even if save fails
      } finally {
        setIsSavingOnNavigation(false);
      }
      
      setCurrentStep(currentStep - 1);
      setInvalidFields([]); // Clear invalid fields when going back
    }
  }, [currentStep, saveAllFields, watch]);

  // Clear invalid fields when user starts typing
  const clearInvalidFields = useCallback(() => {
    setInvalidFields([]);
  }, []);

  const goToStep = useCallback(async (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      // Save current progress before navigating to any step
      setIsSavingOnNavigation(true);
      try {
        const formData = watch();
        await saveAllFields(formData);
        console.log('💾 Progress saved before navigating to step', stepIndex);
      } catch (error) {
        console.error('Failed to save progress before step navigation:', error);
        // Continue to step even if save fails
      } finally {
        setIsSavingOnNavigation(false);
      }
      
      setCurrentStep(stepIndex);
      setInvalidFields([]); // Clear invalid fields when navigating
    }
  }, [saveAllFields, watch]);

  // Save progress manually
  const saveProgress = useCallback(async () => {
    try {
      const formData = watch();
      await saveAllFields(formData);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Save failed:', error);
    }
  }, [watch, saveAllFields]);

  // Handle notifications change (IS-1427 Refactored)
  const handleNotificationsChange = useCallback(async (newNotifications: NotificationRecipient[]) => {
    setNotifications(newNotifications);
    
    // Persist notifications immediately so UI reflects saved state (IS-1427)
    try {
      if (!jiraKey) return;
      await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey,
          updates: { notifications: newNotifications },
        }),
      });
    } catch (error) {
      console.error('Failed to save notifications:', error);
    }
  }, [jiraKey]);

  // IMA-19: Submit must use the same required-field rules as Next/step completion.
  // RHF formState.isValid stays false from stale register()/Controller rules on
  // unmounted steps (e.g. redirectWebsiteUrl after switching to integrated shop),
  // while the wizard correctly treats those fields as optional.
  const canSubmitApplication = STEPS.every((_, index) => isStepComplete(index));

  // Submit form — bypass handleSubmit so RHF errors cannot block a wizard-valid form
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmitApplication || isSubmitting || project?.pendingJiraCreation) {
      return;
    }

    const data = watch();
    setIsSubmitting(true);
    try {
      // Use submitForm instead of updateFormData + updateStatus
      // This calls the submitFormData Cloud Function which handles:
      // - Firestore update
      // - Status change to stage2_provisioning
      // - Jira custom fields sync
      // - Jira status update to "Ready to Build App"
      // - Slack notification
      // - Email confirmation
      // - Status update notifications
      
      // Include notifications in submission (IS-1427 Refactored)
      const submissionData = {
        ...data,
        notifications, // Add notifications array
      };
      
      await submitForm(submissionData as any);
      
      // Clear local storage on successful submission
      clearLocalStorage();
      console.log('🎉 Form submitted successfully - local storage cleared');
      
      onComplete?.(data);
    } catch (error) {
      console.error('Submission failed:', error);
      // Re-throw to show error to user
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepConfig = STEPS[currentStep];
  const CurrentStepComponent = currentStepConfig.component;
  const overallProgress = getOverallCompletion();
  const currentStepProgress = getCurrentStepCompletion();

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🚀 App Onboarding Wizard
        </h1>
        <p className="text-gray-600">
          Let's get your {appType === 'versirent' ? 'VersiRent' : 'IdealOne'} app ready for launch
        </p>
      </div>

      {/* Progress Bars */}
      <div className="mb-8 space-y-4">
        {/* Overall Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Overall Progress
            </span>
            <span className="text-sm text-gray-500">
              {overallProgress}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Current Step Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {STEPS.length}: {currentStepConfig.title}
            </span>
            <span className="text-sm text-gray-500">
              {currentStepProgress}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${currentStepProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="mb-8">
        <div className="flex justify-between">
          {STEPS.map((step, index) => (
            <button
              key={step.id}
              onClick={() => goToStep(index)}
              className={`flex flex-col items-center p-3 rounded-lg transition-all duration-200 ${
                index === currentStep
                  ? 'bg-blue-50 border-2 border-blue-500'
                  : completedSteps.includes(index)
                  ? 'bg-green-50 border-2 border-green-500'
                  : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                index === currentStep
                  ? 'bg-blue-500 text-white'
                  : completedSteps.includes(index)
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {completedSteps.includes(index) ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>
              <span className={`text-xs font-medium text-center ${
                index === currentStep
                  ? 'text-blue-700'
                  : completedSteps.includes(index)
                  ? 'text-green-700'
                  : 'text-gray-600'
              }`}>
                {step.title}
              </span>
              <span className={`text-xs mt-1 ${
                index === currentStep
                  ? 'text-blue-600'
                  : completedSteps.includes(index)
                  ? 'text-green-600'
                  : 'text-gray-500'
              }`}>
                {`${getStepCompletion(index)}% complete`}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Current Step Component */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                {currentStepConfig.title}
              </h2>
              <p className="text-gray-600">
                {currentStepConfig.description}
              </p>
            </div>
            
            <WizardValidationBanner
              invalidFields={invalidFields}
              fieldDefinitions={formConfig.fields}
            />

            {currentStepConfig.id === 'notifications' ? (
              <CurrentStepComponent 
                appType={appType} 
                invalidFields={invalidFields}
                onFieldChange={clearInvalidFields}
                projectData={project ? { ...project, notifications } : project}
                onNotificationsChange={handleNotificationsChange}
              />
            ) : currentStepConfig.id === 'shop' ? (
              <CurrentStepComponent 
                appType={appType}
                invalidFields={invalidFields}
                onFieldChange={clearInvalidFields}
              />
            ) : (
              <CurrentStepComponent 
                step={currentStepConfig}
                fields={formConfig.fields}
                jiraKey={jiraKey}
                invalidFields={invalidFields}
                onFieldChange={clearInvalidFields}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6">
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  disabled={isSavingOnNavigation}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </button>
              )}
              
              <button
                type="button"
                onClick={saveProgress}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Progress
              </button>
            </div>

            <div className="flex items-center space-x-3">
              {/* Save status indicators */}
              {(isSaving || isSavingOnNavigation) && (
                <span className="text-sm text-blue-600 flex items-center">
                  <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isSavingOnNavigation ? 'Saving progress...' : 'Saving...'}
                </span>
              )}
              
              {saveError && (
                <span className="text-sm text-red-600">
                  Save failed: {saveError}
                </span>
              )}
              
              {!isSaving && !saveError && (lastSaved || autoSaveLastSaved) && (
                <span className="text-sm text-green-600">
                  Last saved: {(lastSaved || autoSaveLastSaved)?.toLocaleTimeString()}
                </span>
              )}
              
              {hasLocalData && lastLocalSave && (
                <span className="text-sm text-gray-500">
                  Local backup: {lastLocalSave.toLocaleTimeString()}
                </span>
              )}
              
              {currentStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={isSavingOnNavigation}
                  className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingOnNavigation ? 'Saving...' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={!canSubmitApplication || isSubmitting || project?.pendingJiraCreation}
                    className="flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      project?.pendingJiraCreation
                        ? 'Project setup is not yet complete'
                        : !canSubmitApplication
                          ? 'Complete all required fields before submitting'
                          : ''
                    }
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                  {project?.pendingJiraCreation && (
                    <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                      ⏳ Your project is being set up. You can fill the form now, but submission will be available once setup is complete.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
